import type React from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router"

import { Page } from "@/components/page"
import { PageTitle } from "@/components/page-title"
import type { PageLayoutMode } from "@/utils/page-layout-mode"
import { PAGE_LAYOUT_FILL } from "@/utils/page-layout-mode"
import { useUrl } from "@/utils/url"

import { Box, Link as MuiLink, Stack } from "@mui/material"

import { SiteSecondaryNav } from "./site-secondary-nav"

interface SiteExplorerShellProps {
  actions?: React.ReactNode
  children: React.ReactNode
  heading: string
  layoutMode: PageLayoutMode
}

export const SiteExplorerShell: React.FC<SiteExplorerShellProps> = ({
  actions,
  children,
  heading,
  layoutMode,
}) => {
  const { t: tCommon } = useTranslation("common")
  const { buildSitesUrl } = useUrl()
  const isFill = layoutMode === PAGE_LAYOUT_FILL

  return (
    <Page fullHeight={isFill}>
      <Stack
        sx={{
          gap: 2,
          ...(isFill ? { height: "100%", minHeight: 0 } : {}),
        }}
      >
        <PageTitle
          actions={actions}
          breadcrumbActions={
            <MuiLink component={Link} to={buildSitesUrl()}>
              {tCommon("moduleTitle.sites")}
            </MuiLink>
          }
          heading={heading}
        />
        <Stack
          sx={{
            flexDirection: { xs: "column", md: "row" },
            ...(isFill ? { flex: 1, minHeight: 0 } : { minHeight: "100%" }),
          }}
        >
          <Box
            sx={{
              alignSelf: "flex-start",
              flexShrink: 0,
              position: { md: "sticky" },
              // Sit below sticky PageHeader (76) + divider (1) + Page padding (24).
              top: 101,
              zIndex: { md: 1 },
            }}
          >
            <SiteSecondaryNav />
          </Box>
          {/*
            Do not put overflow clipping on this column — shadows must remain
            visible. Fill mode bounds height via Page fullHeight; scroll mode
            grows with content and uses the page scrollport.
          */}
          <Box
            data-site-layout={layoutMode}
            sx={{
              flex: 1,
              minWidth: 0,
              ...(isFill ? { minHeight: 0, position: "relative" } : {}),
            }}
          >
            {children}
          </Box>
        </Stack>
      </Stack>
    </Page>
  )
}
